import { useEffect, useMemo } from 'react';
import type { TableData } from '@/components/custom/erp/data-table-v2';
import { resolveLedgerDocumentEntry } from '../utils/document-entry';
import { SoChiTietCongNoTheoKhachHangItem } from '@/types/schemas';
import { formatDate, formatMoney } from '@/lib/formatUtils';
import type { SearchFormValues } from '../schema';
import { QUERY_KEYS } from '@/constants';
import { useReport } from '@/hooks';

export interface UseTableDataReturn {
  tables: TableData[];
  loading: boolean;
  totalItems: number;
  currentPage: number;
  handlePageChange: (page: number) => Promise<void>;
  refreshData: () => Promise<void>;
  handleRowClick: (params: MuaHangRecord) => void;
}

export function useTableData(
  searchParams: SearchFormValues | null,
  handleDocumentFormClick?: (row: MuaHangRecord) => void
): UseTableDataReturn {
  const { data, isLoading, totalItems, currentPage, handlePageChange, getReport, refreshData } = useReport<
    SoChiTietCongNoTheoKhachHangItem,
    SearchFormValues
  >({
    endpoint: QUERY_KEYS.SO_CHI_TIET_CONG_NO_THEO_KHACH_HANG,
    pageSize: 50
  });

  useEffect(() => {
    if (searchParams) {
      getReport(searchParams);
    }
  }, [searchParams, getReport]);

  const isSummaryRow = (dienGiai: string | undefined): boolean => {
    if (!dienGiai) return false;
    return ['Đầu kỳ', 'Tổng phát sinh', 'Cuối kỳ'].includes(dienGiai.trim());
  };

  const tables = useMemo(() => {
    const tableData: TableData[] = [
      {
        name: '',
        columns: [
          {
            field: 'ma_unit',
            headerName: 'Đơn vị',
            width: 150,
            renderCell: (params: MuaHangRecord) => {
              return params.row.ma_unit || '';
            },
            cellClassName: (params: MuaHangRecord) => (params.row.isSummaryRow ? 'font-bold' : '')
          },
          {
            field: 'ngay_ct',
            headerName: 'Ngày c/từ',
            width: 120,
            renderCell: (params: MuaHangRecord) => {
              return formatDate(params.row.ngay_ct);
            },
            cellClassName: (params: MuaHangRecord) => (params.row.isSummaryRow ? 'font-bold' : '')
          },
          {
            field: 'so_ct',
            headerName: 'Số c/từ',
            width: 200,
            renderCell: (params: MuaHangRecord) => {
              const documentEntry = resolveLedgerDocumentEntry(params.row);
              if (params.row.isSummaryRow || !handleDocumentFormClick || !params.row.so_ct || !documentEntry) {
                return params.row.so_ct || '';
              }
              return (
                <button
                  type='button'
                  onClick={event => {
                    event.stopPropagation();
                    handleDocumentFormClick(params.row);
                  }}
                  className='hover:text-blue-500 hover:underline'
                >
                  {params.row.so_ct}
                </button>
              );
            },
            cellClassName: (params: MuaHangRecord) => (params.row.isSummaryRow ? 'font-bold' : '')
          },
          {
            field: 'ngay_ct0',
            headerName: 'Ngày hóa đơn',
            width: 120,
            renderCell: (params: MuaHangRecord) => {
              return params.row.ngay_ct0 ? formatDate(params.row.ngay_ct0) : '';
            },
            cellClassName: (params: MuaHangRecord) => (params.row.isSummaryRow ? 'font-bold' : '')
          },
          {
            field: 'so_ct0',
            headerName: 'Số hóa đơn',
            width: 200,
            renderCell: (params: MuaHangRecord) => {
              const documentEntry = resolveLedgerDocumentEntry(params.row);
              if (params.row.isSummaryRow || !handleDocumentFormClick || !params.row.so_ct0 || !documentEntry) {
                return params.row.so_ct0 || '';
              }
              return (
                <button
                  type='button'
                  onClick={event => {
                    event.stopPropagation();
                    handleDocumentFormClick(params.row);
                  }}
                  className='hover:text-blue-500 hover:underline'
                >
                  {params.row.so_ct0}
                </button>
              );
            },
            cellClassName: (params: MuaHangRecord) => (params.row.isSummaryRow ? 'font-bold' : '')
          },
          {
            field: 'tk',
            headerName: 'Tài khoản',
            width: 100,
            renderCell: (params: MuaHangRecord) => {
              // Hide tk for summary rows
              if (params.row.isSummaryRow) {
                return '';
              }
              return params.row.tk || '';
            },
            cellClassName: (params: MuaHangRecord) => (params.row.isSummaryRow ? 'font-bold' : '')
          },
          {
            field: 'tk_du',
            headerName: 'Tk đối ứng',
            width: 120,
            renderCell: (params: MuaHangRecord) => {
              return params.row.tk_du || '';
            },
            cellClassName: (params: MuaHangRecord) => (params.row.isSummaryRow ? 'font-bold' : '')
          },
          {
            field: 'dien_giai',
            headerName: 'Diễn giải',
            width: 200,
            renderCell: (params: MuaHangRecord) => {
              return params.row.dien_giai || '';
            },
            cellClassName: (params: MuaHangRecord) => (params.row.isSummaryRow ? 'font-bold' : '')
          },
          {
            field: 'ps_no',
            headerName: 'Ps nợ',
            width: 120,
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            renderCell: (params: MuaHangRecord) => {
              return formatMoney(params.row.ps_no ?? 0);
            },
            cellClassName: (params: MuaHangRecord) => (params.row.isSummaryRow ? 'font-bold' : '')
          },
          {
            field: 'ps_co',
            headerName: 'Ps có',
            width: 120,
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            renderCell: (params: MuaHangRecord) => {
              return formatMoney(params.row.ps_co ?? 0);
            },
            cellClassName: (params: MuaHangRecord) => (params.row.isSummaryRow ? 'font-bold' : '')
          },
          {
            field: 'ma_ct',
            headerName: 'Mã c/từ',
            width: 120,
            renderCell: (params: MuaHangRecord) => {
              return params.row.ma_ct || '';
            },
            cellClassName: (params: MuaHangRecord) => (params.row.isSummaryRow ? 'font-bold' : '')
          }
        ],
        rows: data.map((item, index) => {
          const summaryRow = isSummaryRow(item.dien_giai);
          return {
            ...item,
            id: item.id || item.xid || `row-${index}`,
            ma_unit: item.ma_unit || '',
            ngay_ct: item.ngay_ct || '',
            so_ct: item.so_ct || '',
            ngay_ct0: item.ngay_ct0 || '',
            so_ct0: item.so_ct0 || '',
            tk: item.tk || '',
            tk_du: item.tk_du || '',
            dien_giai: item.dien_giai || '',
            ps_no: item.ps_no ?? 0,
            ps_co: item.ps_co ?? 0,
            ma_ct: item.ma_ct || '',
            isSummaryRow: summaryRow
          };
        })
      }
    ];

    return tableData;
  }, [data, handleDocumentFormClick]);

  const handleRowClick = (params: MuaHangRecord) => {
    console.log('Row clicked:', params);
  };

  return {
    tables,
    loading: isLoading,
    totalItems,
    currentPage,
    handlePageChange,
    refreshData,
    handleRowClick
  };
}
